import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";

const DemoBrandConfirmationView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const [showConfetti, setShowConfetti] = useState(true);

  // Get the most recent item from state
  const product = state.lastAddedProduct;
  const item = {
    name: product?.name || "Demo Brand Item",
    estimatedValue: product?.rewardValue || 45,
  };

  useEffect(() => {
    // Hide confetti after a few seconds
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const handleDone = () => {
    dispatch({ type: "RESET" });
    navigate("/demobrand");
  };

  const handleTradeAnother = () => {
    navigate("/demobrand/scan");
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex flex-col relative overflow-hidden">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-3 h-3 rounded-full"
              style={{
                backgroundColor: ["#0066CC", "#22C55E", "#FFD700", "#FF6B6B"][
                  i % 4
                ],
                left: `${Math.random() * 100}%`,
              }}
              initial={{ y: -20, opacity: 1 }}
              animate={{
                y: "100vh",
                opacity: 0,
                rotate: Math.random() * 360,
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                delay: Math.random() * 0.5,
                ease: "easeOut",
              }}
            />
          ))}
        </div>
      )}

      {/* Header */}
      <header className="py-6 px-8 flex items-center justify-end relative z-10">
        <div className="flex items-center gap-3">
          <span className="text-white font-semibold text-xl">Demo Brand</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 pb-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, type: "spring" }}
          className="text-center mb-8"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.4, delay: 0.2, type: "spring" }}
            className="w-24 h-24 bg-[#22C55E] rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </motion.div>

          <h1 className="text-4xl font-bold text-white mb-4">Thank You!</h1>
          <p className="text-xl text-white/60 max-w-md mx-auto mb-8">
            Your trade-in has been processed successfully.
          </p>
        </motion.div>

        {/* Credit Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="bg-gradient-to-br from-[#0066CC] to-[#004C99] rounded-2xl p-6 max-w-sm w-full mb-8 shadow-xl"
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-white/80 text-sm font-medium">
              Store Credit Added
            </span>
            <svg
              className="w-6 h-6 text-white/60"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <div className="text-4xl font-bold text-white mb-2">
            ${item.estimatedValue}
          </div>
          <p className="text-white/60 text-sm">For: {item.name}</p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.6 }}
          className="flex flex-col gap-4 w-full max-w-sm"
        >
          <button
            onClick={handleTradeAnother}
            className="w-full px-8 py-4 rounded-lg font-bold tracking-wider border border-white/20 text-white hover:bg-white/5 transition-colors"
          >
            Trade Another Item
          </button>
          <button
            onClick={handleDone}
            className="w-full bg-white text-[#0A1628] px-8 py-4 rounded-lg font-bold tracking-wider shadow-xl hover:bg-gray-100 transition-colors"
          >
            Done
          </button>
        </motion.div>

        {/* Receipt Info */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.8 }}
          className="text-white/40 text-sm mt-8 text-center"
        >
          A confirmation email has been sent to your registered email address.
        </motion.p>
      </main>

      {/* Footer */}
      <footer className="py-4 px-8 text-center text-white/40 text-sm border-t border-white/10 relative z-10">
        Powered by Pentatonic AI
      </footer>
    </div>
  );
};

export default DemoBrandConfirmationView;
